import axios from "axios";
import type {
  AdminResource,
  AdminUser,
  ApiResponse,
  ContentItem,
  LandingContent,
  NavigationItem,
  Product,
  PublicLandingPayload,
  SocialLink,
  Statistic,
  WeddingInvitation,
} from "../types/content";

const baseURL = import.meta.env.VITE_API_URL || "/api/v1";

export const api = axios.create({
  baseURL,
  timeout: 12_000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("steria_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !location.pathname.endsWith("/login")) {
      localStorage.removeItem("steria_admin_token");
      const loginPath = location.pathname.startsWith("/apps/n1master/admin")
        ? "/apps/n1master/admin/login"
        : "/admin/login";
      location.assign(loginPath);
    }
    return Promise.reject(error);
  },
);

export async function getLandingPage() {
  const { data } = await api.get<ApiResponse<PublicLandingPayload>>("/public/landing-page");
  return data.data;
}

export async function getProduct(slug: string) {
  const { data } = await api.get<ApiResponse<Product>>(`/public/products/${slug}`);
  return data.data;
}

export async function getWeddingInvitation(slug = "v1") {
  const { data } = await api.get<ApiResponse<WeddingInvitation>>(`/public/wedding/${slug}`);
  return data.data;
}

export async function loginWithGoogle(idToken: string) {
  const { data } = await api.post<ApiResponse<{ token: string; user: AdminUser }>>(
    "/auth/login",
    { idToken },
  );
  localStorage.setItem("steria_admin_token", data.data.token);
  return data.data;
}

export async function loginWithPassword(username: string, password: string) {
  const { data } = await api.post<ApiResponse<{ token: string; user: AdminUser }>>(
    "/auth/login/password",
    { username, password },
  );
  localStorage.setItem("steria_admin_token", data.data.token);
  return data.data;
}

export async function getMe() {
  const { data } = await api.get<ApiResponse<AdminUser>>("/auth/me");
  return data.data;
}

export async function logout() {
  await api.post("/auth/logout").catch(() => undefined);
  localStorage.removeItem("steria_admin_token");
}

export async function getAdminLanding() {
  const { data } = await api.get<ApiResponse<LandingContent>>("/admin/landing-page");
  return data.data;
}

export async function updateAdminLanding(content: LandingContent) {
  const { data } = await api.put<ApiResponse<LandingContent>>("/admin/landing-page", content);
  return data.data;
}

export async function getAdminWedding(slug = "v1") {
  const { data } = await api.get<ApiResponse<WeddingInvitation>>(`/admin/wedding/${slug}`);
  return data.data;
}

export async function updateAdminWedding(invitation: WeddingInvitation) {
  const { data } = await api.put<ApiResponse<WeddingInvitation>>(
    `/admin/wedding/${invitation.slug}`,
    invitation,
  );
  return data.data;
}

export async function getAdminProducts() {
  const { data } = await api.get<ApiResponse<Product[]>>("/admin/products");
  return data.data;
}

export async function getAdminProduct(id: string) {
  const { data } = await api.get<ApiResponse<Product>>(`/admin/products/${id}`);
  return data.data;
}

export async function saveProduct(product: Partial<Product>) {
  if (product.id) {
    const { data } = await api.put<ApiResponse<Product>>(`/admin/products/${product.id}`, product);
    return data.data;
  }
  const { data } = await api.post<ApiResponse<Product>>("/admin/products", product);
  return data.data;
}

export async function deleteProduct(id: string) {
  await api.delete(`/admin/products/${id}`);
}

type ResourceItem = ContentItem | Statistic | NavigationItem | SocialLink;

export async function getResource<T extends ResourceItem>(resource: AdminResource) {
  const { data } = await api.get<ApiResponse<T[]>>(`/admin/${resource}`);
  return data.data;
}

export async function saveResource<T extends ResourceItem>(
  resource: AdminResource,
  item: Partial<T>,
) {
  const { data } = item.id
    ? await api.put<ApiResponse<T>>(`/admin/${resource}/${item.id}`, item)
    : await api.post<ApiResponse<T>>(`/admin/${resource}`, item);
  return data.data;
}

export async function deleteResource(resource: AdminResource, id: string) {
  await api.delete(`/admin/${resource}/${id}`);
}
